import { Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { EvalLayout } from '@/components/eval/EvalLayout';
import { ExecutionDetailModal } from '@/components/eval/ExecutionDetailModal';
import {
  type GroupBy,
  ResultsFilterBar,
  ResultsGroupedView,
  ResultsTable,
} from '@/components/eval/results';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

interface EvalResult {
  id: string;
  prompt_id: string;
  model_config_id: string;
  is_valid_json: boolean;
  error?: string;
  execution_time_ms?: number;
  created_at: string;
  prompt?: any;
  model_config?: any;
  eval_run?: any;
  evaluation_ok?: boolean;
  formula_errors?: boolean;
  stages?: any;
  last_stage?: string;
  stage_errors?: any;
  engine_checks?: any;
  api_call_error?: string | null;
  json_parse_error?: string | null;
  schema_validation_error?: any[] | null;
  repair_error?: string | null;
  formula_evaluation_error?: string | null;
  sheet_json?: any;
  raw_output?: string;
  compute_values?: any;
  token_usage?: any;
}

interface ResultGroup {
  key: string;
  label: string;
  items: EvalResult[];
  validCount: number;
  totalCount: number;
  run: any;
}

const ResultsPageContent = () => {
  const searchParams = useSearchParams();
  const navigate = useNavigate();

  const [results, setResults] = useState<EvalResult[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selectedResultFull, setSelectedResultFull] =
    useState<EvalResult | null>(null);
  const [loadingSelectedResult, setLoadingSelectedResult] = useState(false);

  // Fetch full result when a result is selected
  useEffect(() => {
    if (!selectedResultId) {
      setSelectedResultFull(null);
      return;
    }

    async function fetchFullResult() {
      try {
        setLoadingSelectedResult(true);
        const response = await apiRequest(
          `/api/eval/results/${selectedResultId}`
        );
        setSelectedResultFull(response.result);
      } catch (error) {
        logError('Failed to fetch full result', error);
        alert('Failed to load result details');
        setSelectedResultId(null);
      } finally {
        setLoadingSelectedResult(false);
      }
    }

    fetchFullResult();
  }, [selectedResultId]);

  // Filters from URL
  const [groupBy, setGroupBy] = useState<GroupBy>(
    (searchParams[0].get('groupBy') as GroupBy) || 'none'
  );
  const [runFilter, setRunFilter] = useState(
    searchParams[0].get('run') || 'all'
  );
  const [searchText, setSearchText] = useState(
    searchParams[0].get('search') || ''
  );
  const [promptFilter, setPromptFilter] = useState(
    searchParams[0].get('prompt') || 'all'
  );
  const [configFilter, setConfigFilter] = useState(
    searchParams[0].get('config') || 'all'
  );
  const [enabledFilter, setEnabledFilter] = useState(
    searchParams[0].get('enabled') || 'all'
  );

  // Lists for filters
  const [prompts, setPrompts] = useState<any[]>([]);
  const [configs, setConfigs] = useState<any[]>([]);

  // Filter configs by enabled status
  const filteredConfigs = useMemo(() => {
    if (enabledFilter === 'enabled') {
      return configs.filter(c => c.enabled !== false);
    } else if (enabledFilter === 'disabled') {
      return configs.filter(c => c.enabled === false);
    }
    return configs;
  }, [configs, enabledFilter]);

  useEffect(() => {
    loadData();
  }, [runFilter, promptFilter, configFilter]);

  async function loadData() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (runFilter !== 'all') params.set('run_id', runFilter);
      if (promptFilter !== 'all') params.set('prompt_id', promptFilter);
      if (configFilter !== 'all') params.set('config_id', configFilter);
      if (searchText) params.set('search', searchText);

      const [resultsData, promptsData, configsData, runsData] =
        await Promise.all([
          apiRequest(`/api/eval/results?${params.toString()}`),
          apiRequest('/api/eval/prompts'),
          apiRequest('/api/eval/configs'),
          apiRequest('/api/eval/runs'),
        ]);

      setResults(resultsData.results || []);
      setPrompts(promptsData.prompts || []);
      setConfigs(configsData.configs || []);
      setRuns(runsData.runs || []);
    } catch (e) {
      logError('Error loading results data', e);
    } finally {
      setLoading(false);
    }
  }

  // Update URL when filters change
  function updateFilters(updates: Record<string, string>) {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(updates).forEach(([key, value]) => {
      if (value === 'all' || value === 'none' || !value) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    navigate(`/eval/results?${params.toString()}`);
  }

  // Grouped data
  const groupedResults = useMemo((): ResultGroup[] | null => {
    if (groupBy === 'none') return null;

    if (groupBy === 'run') {
      return runs
        .map(run => {
          const runResults = results.filter(r => r.eval_run?.id === run.id);
          return {
            key: run.id,
            label: run.name || 'Unknown Run',
            items: runResults,
            validCount: runResults.filter(r => r.is_valid_json && !r.error)
              .length,
            totalCount: runResults.length,
            run: run,
          };
        })
        .sort((a, b) => {
          const aDate = new Date(a.run.created_at || 0).getTime();
          const bDate = new Date(b.run.created_at || 0).getTime();
          return bDate - aDate;
        });
    }

    const groups: Record<string, EvalResult[]> = {};

    results.forEach(result => {
      let key: string;

      if (groupBy === 'prompt') {
        key = result.prompt_id;
      } else if (groupBy === 'config') {
        key = result.model_config_id;
      } else if (groupBy === 'date') {
        const date = new Date(result.created_at);
        key = date.toISOString().split('T')[0];
      } else {
        return;
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    });

    return Object.entries(groups).map(([key, items]) => {
      const firstItem = items[0];
      return {
        key,
        label: firstItem.prompt?.name || firstItem.model_config?.name || key,
        items,
        validCount: items.filter(r => r.is_valid_json && !r.error).length,
        totalCount: items.length,
        run: null,
      };
    });
  }, [results, runs, groupBy]);

  return (
    <EvalLayout>
      <div>
        <header className="mb-8">
          <h1
            className="font-serif text-3xl font-medium m-0"
            style={{ color: '#1A1A1A' }}
          >
            Results Explorer
          </h1>
          <p className="mt-2 text-sm" style={{ color: '#6B6B6B' }}>
            {results.length} execution{results.length !== 1 ? 's' : ''} found
          </p>
        </header>

        <ResultsFilterBar
          groupBy={groupBy}
          runFilter={runFilter}
          promptFilter={promptFilter}
          configFilter={configFilter}
          enabledFilter={enabledFilter}
          searchText={searchText}
          onGroupByChange={v => {
            setGroupBy(v);
            updateFilters({ groupBy: v });
          }}
          onRunFilterChange={v => {
            setRunFilter(v);
            updateFilters({ run: v });
          }}
          onPromptFilterChange={v => {
            setPromptFilter(v);
            updateFilters({ prompt: v });
          }}
          onConfigFilterChange={v => {
            setConfigFilter(v);
            updateFilters({ config: v });
          }}
          onEnabledFilterChange={v => {
            setEnabledFilter(v);
            updateFilters({ enabled: v });
          }}
          onSearchChange={setSearchText}
          onSearchSubmit={() => {
            updateFilters({ search: searchText });
            loadData();
          }}
          runs={runs}
          prompts={prompts}
          configs={filteredConfigs}
        />

        {/* Results */}
        {loading ? (
          <div className="text-center py-16" style={{ color: '#A0A0A0' }}>
            Loading results...
          </div>
        ) : results.length === 0 ? (
          <div
            className="bg-white rounded-xl text-center py-16 px-8"
            style={{ border: '1px solid #E8E0D8' }}
          >
            <p className="m-0" style={{ color: '#A0A0A0' }}>
              No results found. Try adjusting your filters or run some
              evaluations.
            </p>
          </div>
        ) : groupBy === 'none' ? (
          <ResultsTable results={results} onResultClick={setSelectedResultId} />
        ) : (
          <ResultsGroupedView
            groups={groupedResults || []}
            groupBy={groupBy}
            onResultClick={setSelectedResultId}
          />
        )}

        {/* Detail Modal */}
        {(selectedResultId || selectedResultFull) && (
          <ExecutionDetailModal
            result={selectedResultFull || ({ id: selectedResultId } as any)}
            onClose={() => setSelectedResultId(null)}
            loading={loadingSelectedResult}
          />
        )}
      </div>
    </EvalLayout>
  );
};

export default function ResultsPage() {
  return (
    <Suspense
      fallback={
        <div className="py-16 text-center" style={{ color: '#A0A0A0' }}>
          Loading...
        </div>
      }
    >
      <ResultsPageContent />
    </Suspense>
  );
}
